import { t, Trans } from '@lingui/macro';
import { CheckCircle2, Hourglass, Upload, RefreshCw } from "lucide-react";
import { useReducer, useState, useEffect } from "react";
import { DocuBot, DocuBotFile } from "../../../models/DocuBot";
import { DocSummary } from "../../../models/DTO";
import { Room } from "../../../models/Room";
import { Fire } from "../../../services/FireClient";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { Card, CardContent } from "../../../components/ui/card";
import { Progress } from "../../../components/ui/progress";
import { useToast } from "../../../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

export const DocumentBotEditModal: React.FC<{ room: Room, bot: DocuBot, onDone: () => void, onRemove: () => void }> = ({ room, bot, onDone, onRemove }) => {
  const [show, setShow] = useState(true);
  const [displayText, setDisplayText] = useState(t`Upload PDF documents to start training the bot.`);
  const [inProgress, setInProgress] = useState(false);
  const { toast } = useToast();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const hasBeenTrained = bot.files?.length > 0;
  const [summaries, setSummaries] = useState<DocSummary[] | null>(null);

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ title: string, message: string | React.ReactNode, onConfirm?: () => void, confirmText?: string, showCancel?: boolean }>({ title: '', message: '' });

  useEffect(() => {
    async function init() {
      setSummaries(await Fire.getBotSummaries(bot));
    }
    if (show) init();
  }, [show, bot]);

  function showAlert(title: string, message: string | React.ReactNode, onConfirm?: () => void, confirmText = t`OK`, showCancel = false) {
    setAlertConfig({ title, message, onConfirm, confirmText, showCancel });
    setAlertOpen(true);
  }

  function handleClose() {
    setShow(false);
    // Wait for animation
    setTimeout(() => {
      onDone();
    }, 300);
  }

  return (
    <>
      <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-4 border-b flex flex-row items-center justify-between space-y-0">
            <Button variant="ghost" onClick={handleClose} disabled={inProgress}>
              <Trans>Done</Trans>
            </Button>
            <DialogTitle>{bot.name}</DialogTitle>
            <Button variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={remove} disabled={inProgress}>
              <Trans>Delete</Trans>
            </Button>
          </DialogHeader>

          {inProgress && <Progress value={undefined} className="h-1 rounded-none w-full" />}

          <div className="flex-1 bg-muted/50 overflow-y-auto p-4">
            <div className="space-y-4 max-w-2xl mx-auto">
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bot-name"><Trans>Name of the bot</Trans></Label>
                    <Input
                      id="bot-name"
                      value={bot.name}
                      onChange={e => { bot.name = e.target.value; forceUpdate(); }}
                      maxLength={36}
                      disabled={bot.isProcessing || inProgress}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bot-instructions">
                      <Trans>Instructions after the prompt</Trans> <span className="text-muted-foreground font-normal"><Trans>(optional)</Trans></span>
                    </Label>
                    <Input
                      id="bot-instructions"
                      value={bot.afterPrompt}
                      onChange={e => { bot.afterPrompt = e.target.value; forceUpdate(); }}
                      maxLength={350}
                      disabled={bot.isProcessing || inProgress}
                      placeholder={t`e.g. Explain in detail.`}
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-2">
                    <Checkbox
                      id="hide-files"
                      checked={bot.hideFiles}
                      onCheckedChange={(checked) => { bot.hideFiles = !!checked; forceUpdate(); }}
                      disabled={inProgress}
                    />
                    <Label htmlFor="hide-files" className="cursor-pointer font-normal"><Trans>Hide documents from users</Trans></Label>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="text-sm text-[color:rgb(var(--text-body))] font-medium">{displayText}</div>

                  {bot.files.length > 0 && (
                    <div className="space-y-2 border rounded-md p-2 bg-background">
                      {bot.files.map((f, i) => (
                        <div key={`${f.url}-${i}`} className="flex items-center gap-2 text-sm p-1">
                          {f.isTrained ?
                            <CheckCircle2 className="w-4 h-4 text-green-500" /> :
                            <Hourglass className="w-4 h-4 text-muted-foreground" />
                          }
                          <span className="truncate">{f.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-2">
                    {!bot.isProcessing && (
                      <Button
                        variant="outline"
                        disabled={inProgress}
                        onClick={() => document.getElementById('bot-file-input')?.click()}
                        className="gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        {hasBeenTrained ? <Trans>Add another PDF document</Trans> : <Trans>Upload PDF document</Trans>}
                      </Button>
                    )}

                    {!inProgress && (bot.files.length > 0) && (
                      <Button onClick={startTrainingBot} variant="secondary" className="gap-2">
                        <RefreshCw className="w-4 h-4" />
                        <Trans>Redo training</Trans>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {bot.isIndexCreated && (
                <Card>
                  <CardContent className="p-6 space-y-3">
                    <div className="font-semibold text-sm"><Trans>Outline</Trans>: <Trans>{summaries?.length ?? 0} parts</Trans></div>
                    <Button onClick={createSummaries} disabled={inProgress} variant="outline" size="sm">
                      <Trans>Create Outline</Trans>
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      This experimental feature makes it possible to get a quick overview of the content.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            <input
              id="bot-file-input"
              type='file'
              onChange={filesSelected}
              hidden
              accept='.pdf'
              multiple
            />
          </div>

          <div className="p-2 border-t text-meta-sm text-muted-foreground bg-background text-center">
            {bot.id}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">
                {alertConfig.message}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {alertConfig.showCancel && (
              <AlertDialogCancel onClick={() => setAlertOpen(false)}><Trans>Cancel</Trans></AlertDialogCancel>
            )}
            <AlertDialogAction onClick={() => {
              setAlertOpen(false);
              alertConfig.onConfirm?.();
            }}>{alertConfig.confirmText}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  async function filesSelected(e: any) {
    const selectedFiles = e.target.files as File[];
    if (selectedFiles && selectedFiles.length > 0) {
      if (!!bot.files.find(f => f.filename == selectedFiles[0].name)) {
        showAlert(t`Notice`, t`This file has already been uploaded`);
        return;
      }

      const botIndex = room.bots?.findIndex(b => b.id == bot.id) ?? -1;
      if (!room.bots || botIndex < 0) {
        showAlert('Error', `Error: Bot not found in channel.bots.`);
        return;
      }
      setDisplayText(t`Uploading documents...`);

      setInProgress(true);
      const files = await uploadFiles(e.target.files);
      bot.isProcessing = true;
      bot.files = files.concat(bot.files || []);
      e.target.value = '';
      forceUpdate();

      setDisplayText(t`Document has been uploaded. Training is now in progress, stay on this page until it's finished.`);

      room.bots[botIndex] = bot;
      await Fire.updateRoomBots(room.id, room.bots);

      startTrainingBot();
      // done();
    }
  }

  async function startTrainingBot() {
    setInProgress(true);
    try {
      const res = await Fire.trainDocuBot(bot);
      bot.isIndexCreated = res.result == 'SUCCESS';
      bot.isProcessing = false;
      bot.files = bot.files.map(f => {
        f.isTrained = true;
        return f;
      });
      setDisplayText(t`Training is finished. Result ${res.result}`);
      if (res.result == 'ERROR') {
        showAlert('Error', `Error: ${res.message}`);
      }
    } catch (error) {
      const e = error as Error;
      showAlert('Error', `Error: ${e.stack} ${e.message}}`);
      setDisplayText(t`Training failed.`);
    }
    setInProgress(false);
    forceUpdate();
  }

  async function uploadFiles(files: File[]): Promise<DocuBotFile[]> {
    let docuFiles: DocuBotFile[] = [];
    return new Promise(async (resolve, reject) => {
      for (let index = 0; index < files.length; index++) {
        let file = files[index];
        setInProgress(true);
        Fire.uploadFile(file, `/rooms/${room.id}/bots/${bot.id}/${file.name}`, (url) => {
          if (url) {
            docuFiles.push(new DocuBotFile(file.name, url, file.size, file.type));
            if (docuFiles.length === files.length) {
              resolve(docuFiles);
            }
          } else {
            resolve([]);
          }
        }, null);
      }
    });
  }

  function remove() {
    showAlert(
      t`Delete bot`,
      t`Are you sure you want to delete this bot?`,
      () => {
        setShow(false);
        setTimeout(() => {
          onRemove();
        }, 300);
      },
      t`Delete`,
      true
    );
  }

  async function createSummaries() {
    toast({ title: t`Notice`, description: t`This may take several minutes depending on how large documents you have uploaded.`, duration: 3000 });

    setInProgress(true);
    try {
      await Fire.createSummaries(bot);
      toast({ title: t`Success`, description: t`Outline has been created. Click on the bot and click on "Outline".`, duration: 3000 });
      setSummaries(await Fire.getBotSummaries(bot))
    } catch (error) {
      showAlert('Error', `Error: ${error}`);
    }
    setInProgress(false);
  }
}